/**
 * Posteingang-Assistent S2, Entscheidung P6 (Antoine 26.09.2026): Messung der KI-Zuordnung bei
 * mehreren Vorgaengen + Schalter „Automatik“. Konsumiert GET/POST /api/inbox/ki-zuordnung bzw.
 * /api/inbox/ki-automatik (routes/inbox.ts). Der Schalter steht AUS und laesst sich erst nach
 * 14 Tagen Schattenbetrieb einschalten (Gate in der Datenbank, hier nur angezeigt).
 */
import { useState } from "react";
import { useApi, apiMutate } from "../hooks/useApi";
import { formatDate } from "./format";
import { ErrorNote, Icon, KpiCard, LoadingRows, Toggle } from "./components";

interface Band {
  gesamt: number;
  entschieden: number;
  treffer: number;
}

export interface KiZuordnungMessung {
  verfuegbar: boolean;
  gesamt?: number;
  entschieden?: number;
  treffer?: number;
  quote?: number | null;
  baender?: { hoch: Band; mittel: Band; niedrig: Band };
  schatten?: { waereAutomatisch: number; entschieden: number; richtig: number };
  automatik?: {
    an: boolean;
    seit: string | null;
    ausGrund: string | null;
    automatischZugeordnet: number;
    zurueckgenommen7Tage: number;
    messungSeit: string | null;
    freischaltbarAb: string | null;
    freischaltbar: boolean;
    darfSchalten: boolean;
  };
}

const prozent = (b: Band) => (b.entschieden > 0 ? `${Math.round((b.treffer / b.entschieden) * 100)} %` : "—");

export function KiZuordnungKarte() {
  const { data, loading, error, reload } = useApi<KiZuordnungMessung>("/api/inbox/ki-zuordnung");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function schalten(an: boolean) {
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate("/api/inbox/ki-automatik", "POST", { an });
      await reload();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const a = data?.automatik;
  const s = data?.schatten;
  const hinweis = !a
    ? null
    : a.an
      ? `Eingeschaltet seit ${formatDate(a.seit)}. Schaltet sich selbst ab, wenn in 7 Tagen mehr als 2 automatische Zuordnungen zurückgenommen werden.`
      : !a.messungSeit
        ? "Die Messung beginnt mit der ersten Mail, die zu mehreren Vorgängen passen könnte. Einschaltbar 14 Tage danach."
        : !a.freischaltbar
          ? `Einschaltbar ab ${formatDate(a.freischaltbarAb)} — nach 14 Tagen Messung.`
          : !a.darfSchalten
            ? "Einschalten dürfen nur Inhaber."
            : "Die 14 Tage Messung sind vorbei. Prüfen Sie die Zahlen, bevor Sie einschalten.";

  return (
    <section className="mt-8 rounded-2xl border border-[#F0E7D6] p-5 dark:border-white/[0.06]" data-testid="ki-zuordnung-section">
      <div className="mb-1 flex items-center gap-2">
        <Icon name="auto_awesome" className="text-[18px] text-primary" />
        <h2 className="font-title-md text-title-md font-semibold text-on-background dark:text-inverse-on-surface">Assistent: Zuordnung bei mehreren Vorgängen</h2>
      </div>
      <p className="mb-4 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        Schreibt ein Kunde mit mehreren offenen Vorgängen, schlägt der Assistent den passenden vor. Zugeordnet wird von
        Hand. Hier sehen Sie, wie oft sein Vorschlag stimmte.
      </p>

      {loading && <LoadingRows rows={2} />}
      {!loading && error && <ErrorNote message={error} title="Messung konnte nicht geladen werden" onRetry={() => void reload()} />}
      {!loading && data && !data.verfuegbar && (
        <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Noch nicht eingerichtet.</p>
      )}

      {!loading && data?.verfuegbar && a && s && data.baender && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <KpiCard label="Vorschläge" value={String(data.gesamt ?? 0)} />
            <KpiCard label="Von Hand entschieden" value={String(data.entschieden ?? 0)} />
            <KpiCard label="Treffer" value={data.quote == null ? "—" : `${String(data.quote).replace(".", ",")} %`} />
            <KpiCard label="Wäre automatisch" value={`${s.richtig} von ${s.entschieden} richtig`} />
          </div>
          <p className="mt-3 font-body-sm text-[12.5px] text-secondary dark:text-secondary-fixed-dim">
            Treffer nach Sicherheit: ab 85 % {prozent(data.baender.hoch)} · 50–85 % {prozent(data.baender.mittel)} · unter 50 %{" "}
            {prozent(data.baender.niedrig)}
            {a.messungSeit ? ` · Messung seit ${formatDate(a.messungSeit)}` : ""}
          </p>

          <div className="mt-5 flex items-start justify-between gap-4 border-t border-outline-variant/30 pt-4 dark:border-outline/30">
            <div className="min-w-0">
              <p className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Automatik</p>
              <p className="mt-0.5 font-body-sm text-[12.5px] text-secondary dark:text-secondary-fixed-dim">
                Ordnet selbst zu, nur bei mindestens 85 % Sicherheit, deutlichem Abstand zum Zweitbesten, einem festen Signal
                (Datum oder Vorgangsnummer in der Mail) und bestätigtem Absender. Jede Zuordnung lässt sich zurücknehmen.
              </p>
              {hinweis && <p className="mt-2 font-body-sm text-[12.5px] text-on-surface-variant">{hinweis}</p>}
              {!a.an && a.ausGrund?.startsWith("selbstabschaltung") && (
                <p className="mt-2 flex items-center gap-1.5 font-body-sm text-[12.5px] text-error">
                  <Icon name="warning" className="text-[14px]" /> Hat sich selbst abgeschaltet: zu viele Zuordnungen wurden zurückgenommen.
                </p>
              )}
            </div>
            <Toggle
              checked={a.an}
              onChange={(an) => void schalten(an)}
              label="Automatik"
              disabled={busy || !a.darfSchalten || (!a.an && !a.freischaltbar)}
            />
          </div>
          {fehler && <ErrorNote message={fehler} />}
        </>
      )}
    </section>
  );
}
